import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Package, PackageOpen, Shirt, Tag, ArrowLeftRight, HandCoins, Layers } from 'lucide-react';
import { usePublicVault } from '@/hooks/useApi';
import CardImage from '@/components/CardImage';

interface Props {
  userId: string;
  username?: string;
}

type VaultTab = 'displayCase' | 'bundleCache' | 'merchGallery';

const CATEGORY_LABEL: Record<string, string> = {
  booster_pack: 'Booster Pack',
  booster_box:  'Booster Box',
  bundle:       'Bundle / Lot',
  merch:        'Merch',
  single_card:  'Single Card',
};

function ItemTags({ is_for_sale, is_for_trade, make_an_offer }: {
  is_for_sale?: boolean;
  is_for_trade?: boolean;
  make_an_offer?: boolean;
}) {
  if (!is_for_sale && !is_for_trade && !make_an_offer) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {is_for_sale && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <Tag className="h-2.5 w-2.5" /> For Sale
        </span>
      )}
      {is_for_trade && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
          <ArrowLeftRight className="h-2.5 w-2.5" /> For Trade
        </span>
      )}
      {make_an_offer && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <HandCoins className="h-2.5 w-2.5" /> Make an Offer
        </span>
      )}
    </div>
  );
}

function DisplayCaseSection({ items }: { items: any[] }) {
  if (items.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-gray-400 dark:text-zinc-600">
        No cards on display yet.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {items.map((item) => (
        <Link
          key={item.id}
          to={item.card?.id ? `/cards/${item.card.id}` : '#'}
          className="group flex flex-col items-center text-center"
        >
          <div className="w-full aspect-[2.5/3.5] rounded-xl overflow-hidden shadow-md group-hover:shadow-lg transition-shadow">
            <CardImage
              src={item.card?.image_url}
              alt={item.card?.name}
              cardName={item.card?.name}
              game={item.card?.game}
              className="w-full h-full"
            />
          </div>
          <p className="text-xs font-medium text-gray-800 dark:text-zinc-200 mt-1.5 truncate w-full">{item.card?.name}</p>
          <p className="text-[10px] text-gray-400 dark:text-zinc-600 truncate w-full">{item.condition}</p>
          <ItemTags
            is_for_sale={item.is_for_sale}
            is_for_trade={item.is_for_trade}
            make_an_offer={item.make_an_offer}
          />
        </Link>
      ))}
    </div>
  );
}

function BulkItemCard({ item }: { item: any }) {
  const icon = item.item_category === 'booster_box'
    ? <PackageOpen className="h-8 w-8 text-brand-400" />
    : item.item_category === 'bundle'
    ? <Layers className="h-8 w-8 text-brand-400" />
    : <Package className="h-8 w-8 text-brand-400" />;

  return (
    <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-xl p-4 shadow-sm flex flex-col gap-1.5">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 dark:text-zinc-100 truncate">{item.item_name}</p>
          {item.item_brand && (
            <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{item.item_brand}</p>
          )}
          <p className="text-[10px] text-gray-400 dark:text-zinc-600 mt-0.5">{CATEGORY_LABEL[item.item_category] ?? item.item_category} · {item.condition}</p>
        </div>
        {item.quantity > 1 && (
          <span className="flex-shrink-0 text-xs font-bold text-brand-600 dark:text-brand-400">×{item.quantity}</span>
        )}
      </div>
      <ItemTags
        is_for_sale={item.is_for_sale}
        is_for_trade={item.is_for_trade}
        make_an_offer={item.make_an_offer}
      />
    </div>
  );
}

function MerchCard({ item }: { item: any }) {
  return (
    <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-xl p-4 shadow-sm flex flex-col gap-1.5">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center">
          <Shirt className="h-8 w-8 text-accent-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 dark:text-zinc-100 truncate">{item.item_name}</p>
          {item.item_brand && (
            <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">{item.item_brand}</p>
          )}
          <p className="text-[10px] text-gray-400 dark:text-zinc-600 mt-0.5">Merch · {item.condition}</p>
        </div>
        {item.quantity > 1 && (
          <span className="flex-shrink-0 text-xs font-bold text-accent-600 dark:text-accent-400">×{item.quantity}</span>
        )}
      </div>
      <ItemTags
        is_for_sale={item.is_for_sale}
        is_for_trade={item.is_for_trade}
        make_an_offer={item.make_an_offer}
      />
    </div>
  );
}

export default function PeekIntoTheVault({ userId, username }: Props) {
  const [activeTab, setActiveTab] = useState<VaultTab>('displayCase');
  const { data, isLoading } = usePublicVault(userId);

  const displayCase   = data?.displayCase   ?? [];
  const bundleCache   = data?.bundleCache   ?? [];
  const merchGallery  = data?.merchGallery  ?? [];

  const hasAnyContent = displayCase.length > 0 || bundleCache.length > 0 || merchGallery.length > 0;

  if (!isLoading && !hasAnyContent) return null;

  const tabs: { key: VaultTab; label: string; count: number; icon: React.ReactNode }[] = [
    { key: 'displayCase',  label: 'Display Case',   count: displayCase.length,  icon: <Eye className="h-4 w-4" /> },
    { key: 'bundleCache',  label: 'Bundle Cache',   count: bundleCache.length,  icon: <PackageOpen className="h-4 w-4" /> },
    { key: 'merchGallery', label: 'Merch Gallery',  count: merchGallery.length, icon: <Shirt className="h-4 w-4" /> },
  ];

  return (
    <section className="mb-10">
      <div className="flex items-center gap-2 mb-5">
        <Eye className="h-5 w-5 text-brand-500" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100">
          Peek into the Vault
        </h2>
        {username && (
          <span className="text-sm text-gray-500 dark:text-zinc-500">— {username}'s collection showcase</span>
        )}
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden">
        {/* Tab bar */}
        <div className="flex border-b border-gray-100 dark:border-zinc-800">
          {tabs.map(({ key, label, count, icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors flex-1 justify-center ${
                activeTab === key
                  ? 'border-b-2 border-brand-500 text-brand-600 dark:text-brand-400 bg-brand-50/40 dark:bg-brand-900/10'
                  : 'text-gray-500 dark:text-zinc-500 hover:text-gray-700 dark:hover:text-zinc-300'
              }`}
            >
              {icon}
              <span className="hidden sm:inline">{label}</span>
              {count > 0 && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                  activeTab === key
                    ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                    : 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500'
                }`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="grid grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton aspect-[2.5/3.5] rounded-xl" />
              ))}
            </div>
          ) : (
            <>
              {activeTab === 'displayCase' && <DisplayCaseSection items={displayCase} />}

              {activeTab === 'bundleCache' && (
                bundleCache.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400 dark:text-zinc-600">
                    No bundles or booster boxes yet.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {bundleCache.map((item) => (
                      <BulkItemCard key={item.id} item={item} />
                    ))}
                  </div>
                )
              )}

              {activeTab === 'merchGallery' && (
                merchGallery.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400 dark:text-zinc-600">
                    No merch items yet.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {merchGallery.map((item) => (
                      <MerchCard key={item.id} item={item} />
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
